import { useMemo } from 'react';
import { useSubscriptionCheckout } from 'src/common/hooks/use-subscription-checkout';
import { useTrackEventMutation } from 'src/common/slices';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { useGetSubscriptionPricingQuery } from 'src/features/pricing/pricing.slice';

// See apps/frontend/src/features/campaigns/campaigns.config.tsx:215
const STUDENT_CAMPAIGN_NAME = 'STUDENT30_2025' as const;

export interface GoPremiumModalData {
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyTotal: number;
  currency: string;
  originalYearlyPrice?: number;
  isDiscounted: boolean;
  monthsSaved?: number;
  campaignDiscount?: number;
  isStudentDiscount?: boolean;
  onSubscribe: (billingType: 'annual' | 'monthly') => void;
}

export const useGoPremiumModalData = (): GoPremiumModalData | null => {
  const [trackEvent] = useTrackEventMutation();
  const { campaign } = useActiveCampaign();
  const { openCheckout } = useSubscriptionCheckout();
  const { data: pricingResponse } = useGetSubscriptionPricingQuery(false);
  const pricingData = pricingResponse?.data;

  return useMemo(() => {
    if (!pricingData) return null;

    const { monthlyPlan, yearlyPlan, isStudent, studentDiscount } = pricingData;
    const currency = monthlyPlan.symbol;
    const monthlyPrice = monthlyPlan.price;
    const originalYearlyPrice = yearlyPlan.price / 12;
    const campaignAmount = campaign && campaign.name !== STUDENT_CAMPAIGN_NAME ? campaign.amount : undefined;
    const discount = campaignAmount || (isStudent ? studentDiscount : 0);
    const discountedYearlyPrice = discount ? originalYearlyPrice * ((100 - discount) / 100) : originalYearlyPrice;
    const isYearlyDiscounted = !!discount;
    const isStudentDiscount = !campaignAmount && isStudent && !!studentDiscount;
    const monthsSaved = Math.round((monthlyPlan.price * 12 - discountedYearlyPrice * 12) / monthlyPlan.price);

    const onSubscribe = (billingType: 'annual' | 'monthly') => {
      trackEvent({
        event: 'click-subscribe-now',
        properties: {
          'clicked-on': `go-premium-attackbox-modal-${billingType}`,
          campaign: campaign?.name,
          path: '/room',
        },
      });

      openCheckout({
        planId: billingType === 'annual' ? yearlyPlan.planId : monthlyPlan.planId,
        campaign: campaign?.name,
        interval: billingType === 'annual' ? 'yearly' : 'monthly',
      });
    };

    return {
      monthlyPrice,
      yearlyPrice: discountedYearlyPrice,
      yearlyTotal: discountedYearlyPrice * 12,
      currency,
      originalYearlyPrice: isYearlyDiscounted ? originalYearlyPrice : undefined,
      isDiscounted: isYearlyDiscounted,
      monthsSaved,
      campaignDiscount: campaign && campaign.name !== STUDENT_CAMPAIGN_NAME ? campaign.amount : undefined,
      isStudentDiscount,
      onSubscribe,
    };
  }, [pricingData, campaign, trackEvent, openCheckout]);
};
